import {
  closeBrackets,
  closeBracketsKeymap,
  startCompletion,
} from '@codemirror/autocomplete';
import { defaultKeymap, history, redo, undo } from '@codemirror/commands';
import { syntaxHighlighting, syntaxTree } from '@codemirror/language';
import type { EditorSelection, TransactionSpec } from '@codemirror/state';
import { EditorState, Transaction } from '@codemirror/state';
import {
  drawSelection,
  EditorView,
  keymap,
  placeholder as placeholderExtension,
  tooltips,
} from '@codemirror/view';
import clsx from 'clsx';
import { isEqual } from 'lodash-es';
import {
  Children,
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
  useState,
  type PropsWithChildren,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { ErrorBoundary, useErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';

import { Button } from '@dynatrace/strato-components/buttons';
import {
  _parseBoolean,
  _useAriaLabelingProps,
  type DataTestId,
  type MaskingProps,
  type StylingProps,
} from '@dynatrace/strato-components/core';
import { Flex } from '@dynatrace/strato-components/layouts';
import { Text } from '@dynatrace/strato-components/typography';
import Borders from '@dynatrace/strato-design-tokens/borders';
import { CriticalIcon, FilterIcon, XmarkIcon } from '@dynatrace/strato-icons';

import {
  FilterFieldContext,
  type FilterFieldAutoSuggestionsContextProps,
} from './contexts/FilterFieldContext.js';
import { EscapedCharacterDecorationPlugin } from './decorations/escaped-characters.js';
import { FilterErrorDecorationPlugin } from './decorations/filter-errors.js';
import { FilterExistsDecorationPlugin } from './decorations/filter-exists-operator.js';
import { FilterKeyDecorationPlugin } from './decorations/filter-keys.js';
import { FilterStatementDeleteWidgetPlugin } from './decorations/filter-statements-delete-widget.js';
import { FilterStatementDecorationPlugin } from './decorations/filter-statements.js';
import { FilterValueDecorationPlugin } from './decorations/filter-values.js';
import { FilterVariableDecorationPlugin } from './decorations/filter-variable.js';
import { filterFieldHighlighter } from './decorations/highlight.js';
import {
  ANNOTATION_COMPOSE_END,
  ANNOTATION_DELETE_STATEMENT,
  ANNOTATION_SUGGESTION_APPLY,
  ariaAttributesCompartment,
  disabledPropCompartment,
  overrideKeys,
  placeholderCompartment,
  readOnlyPropCompartment,
  stringAnnotation,
  suggestionsCompartment,
  syntaxModeEffect,
  usedErrors,
  usedSyntaxMode,
  usedValidatorMap,
  validatorMapEffect,
} from './editor-config.js';
import { ErrorFallback } from './error-fallback/ErrorFallback.js';
import * as styles from './FilterField.sty.js';
import { useCachedTreeConversion } from './hooks/useCachedTreeConversion.js';
import { useErrorAwareViewRef } from './hooks/useErrorAwareViewRef.js';
import { useFilterFieldLinting } from './hooks/useFilterFieldLinting.js';
import { useReferencedState } from './hooks/useReferencedState.js';
import { useUpdatableCallback } from './hooks/useUpdatableCallback.js';
import { filterFieldLanguageExtension } from './parser/filterfield-language.js';
import { getNumberParser } from './parser/getNumberParser.js';
import { filterFieldReducer } from './state/filter-field-state-reducer.js';
import type {
  FilterFieldSuggestion,
  FilterFieldSuggestionProps,
} from './suggestions/Suggestion.js';
import { Suggestion } from './suggestions/Suggestion.js';
import { SuggestionDetails } from './suggestions/SuggestionDetails.js';
import { SuggestionGroup } from './suggestions/SuggestionGroup.js';
import { SuggestionGroupLabel } from './suggestions/SuggestionGroupLabel.js';
import type {
  _FilterFieldSuggestionsCallback,
  _FilterFieldSuggestionsContext,
} from './suggestions/suggestions-extension.js';
import { suggestionsExtension } from './suggestions/suggestions-extension.js';
import { Suggestions } from './suggestions/Suggestions.js';
import { SuggestionsOverlay } from './suggestions/SuggestionsOverlay.js';
import type { FilterFieldSuggestionsCallback } from './types/suggestions.js';
import type { FilterFieldTree } from './types/tree-nodes.js';
import type { FilterFieldValidatorMap } from './types/validation.js';
import { autoFormatLogicalOperators } from './utils/auto-format-logical-operators.js';
import {
  getCurrentSuggestion,
  getFirstSuggestion,
  getLastSuggestion,
  getNextSuggestion,
} from './utils/get-next-suggestion.js';
import { getSuggestionTransaction } from './utils/get-suggestion-transaction.js';
import { VisuallyHidden } from '../../core/components/visually-hidden/VisuallyHidden.js';
import { useFormControlState } from '../../core/hooks/useFormControlState.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { usePrevious } from '../../core/hooks/usePrevious.js';
import type { FormControlProps } from '../../core/types/form-control-props.js';
import { isSlot } from '../../core/utils/is-slot.js';
import { editorContainerCSS } from '../../editors/base-code-editor/BaseCodeEditor.sty.js';
import { withFormFieldMessageContextProvider } from '../../forms/forms-core/components/form-field/FormFieldMessageContextProvider.js';
import { InternalBaseItem as BaseItem } from '../../forms/forms-core/components/form-field-messages/FormFieldMessageBaseItem.js';
import { useFormFieldContext } from '../../forms/forms-core/hooks/useFormFieldContext.js';
import { useFormFieldMessagesContext } from '../../forms/forms-core/hooks/useFormFieldMessagesContext.js';
import { getI18nErrorMessage } from '../../forms/forms-core/hooks/useFormFieldValidation.js';
import { FormFieldMessagesAction } from '../../forms/forms-core/state/formfield-messages-state-reducer.js';
import { FormFieldAction } from '../../forms/forms-core/state/formfield-state-reducer.js';
import { useInputHint } from '../../forms/hooks/useInputHint.js';
import type { FormControlWithOverlayRef } from '../../forms/shared-types.js';
import { useOverlayWithTrigger } from '../../overlays/hooks/useOverlayWithTrigger.js';

// TODO: Keyboard navigation in filter field (easy deletion of whole statements etc.).
// TODO: Control label?
// TODO: Check keyboard navigation with empty suggestions overlay again.
// TODO: Test that custom suggestions are shown properly depending on the autoSuggestions prop.
// TODO: Check if we can combine tree conversion and error / diagnostics generation.
// TODO: Suggestions overlay max-height + virtualization.
// TODO: Add test for auto-formatting.
// TODO: Test for correct cursor position when editing in combination with auto-formatting of logical operators.

function isEditorSelection(selection: unknown): selection is EditorSelection {
  return (selection as EditorSelection).ranges !== undefined;
}

// #region typings
/**
 * Accepted properties for FilterField.
 * @public
 */
export type FilterFieldProps = PropsWithChildren<
  StylingProps &
    DataTestId &
    MaskingProps &
    FormControlProps<
      string,
      (value: string, tree: FilterFieldTree, isValid: boolean) => void
    > & {
      /**
       * Placeholder text displayed when the filter field is empty.
       * @defaultValue 'Type to filter'
       */
      placeholder?: string;
      // DEPRECATION: [APPDEV-13114] BREAKING-CHANGE: Remove deprecated `syntax` prop.
      /**
       * The syntax used for parsing the input.
       * The basic syntax uses a narrowed down set of rules, not supporting grouping
       * and logical 'or'. The complex syntax provides the full set of rules.
       * @defaultValue 'complex'
       * @deprecated The basic syntax will no longer be supported after the next breaking change. The syntax will default to `complex` and the `syntax` prop will be removed.
       */
      syntax?: 'basic' | 'complex';
      /**
       * Whether the automatically determined suggestions (logical / comparison operators) should be shown.
       * If set to true, the suggestions will automatically be added to the suggestions overlay.
       * @defaultValue false
       */
      autoSuggestions?: boolean;
      /**
       * Callback triggered when the suggestions may need to be updated.
       * @defaultValue undefined
       */
      onSuggest?: FilterFieldSuggestionsCallback;
      /**
       * Callback triggered when the user submits the currently entered filter for filtering.
       * @defaultValue undefined
       */
      onFilter?: (filterState: {
        /** The current value of the filter field. */
        value: string;
        /** Syntax tree of the current value. */
        syntaxTree: FilterFieldTree;
        /** Whether the current value is valid. */
        isValid: boolean;
      }) => void;
      /** Validators to restrict allowed keys and their operators. */
      validatorMap?: FilterFieldValidatorMap;
    }
>;
// #endregion typings

const _FilterField = forwardRef<FormControlWithOverlayRef, FilterFieldProps>(
  (props, forwardedRef) => {
    const {
      children,
      name,
      value,
      defaultValue,
      required: propRequired = false,
      disabled: propDisabled = false,
      readOnly: propReadOnly = false,
      placeholder: placeholderProp,
      id: propId,
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      syntax = 'complex',
      autoSuggestions = false,
      controlState,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      'aria-disabled': propAriaDisabled = false,
      onChange,
      onSuggest,
      onFilter,
      validatorMap,
    }: FilterFieldProps = props;

    const uniqueId = useId();
    const ariaLabelingProps = _useAriaLabelingProps(props);

    const suggestionsSlot = Children.toArray(children).find((child) =>
      isSlot(child, Suggestions),
    );

    const { getSyntaxTree } = useCachedTreeConversion();

    const intl = useIntl();

    const { showBoundary } = useErrorBoundary();

    const onChangeRef = useUpdatableCallback(onChange);
    const onFilterRef = useUpdatableCallback(onFilter);

    // validatorMap ref to use in everything codemirror related.
    const validatorMapRef = useRef(validatorMap);

    const numberParser = getNumberParser();

    const isUserEventComposeRef = useRef(false);

    // Values from previous render-cycle to trigger updates while rendering.
    // This way, we can avoid using effects, which would trigger another render.
    const [prevValue, setPrevValue] = useState(value);

    const [prevPropDisabled, setPrevPropDisabled] = useState(propDisabled);
    const [prevPropRequired, setPrevPropRequired] = useState(propRequired);
    const [prevPropReadOnly, setPrevPropReadOnly] = useState(propReadOnly);
    const [prevPropId, setPrevPropId] = useState(propId);
    const [prevSyntaxMode, setPrevSyntaxMode] = useState(syntax);

    const [prevPlaceholderProp, setPrevPlaceholderProp] =
      useState(placeholderProp);
    const [prevOnSuggest, setPrevOnSuggest] = useState(() => onSuggest);

    const isControlled = value !== undefined;

    const placeholder = intl.formatMessage({
      defaultMessage: 'Type to filter',
      id: 'GhC5F6ZRFGDVs41W',
      description: 'Default placeholder text shown in the filter field.',
    });
    const contentEditableDivAriaLabel = intl.formatMessage({
      defaultMessage: 'Filter field',
      id: 'swS1rIU8VQep1PDq',
      description: 'Default aria label for the filter field',
    });

    // Suggestions overlay setup.
    const suggestionsOverlayRef = useRef<HTMLDivElement | null>(null);
    const {
      targetRef,
      overlayRef: _overlayRef,
      overlayState,
      overlayProps: _overlayProps,
    } = useOverlayWithTrigger({
      placement: 'bottom-start',
      placementFallbacks: ['top-start'],
      widthStrategy: 'trigger',
      offset: 4,
    });
    const [prevOpen, setPrevOpen] = useState(overlayState.isOpen);
    const prevOpenRef = useRef(prevOpen);
    useEffect(() => {
      prevOpenRef.current = prevOpen;
    }, [prevOpen]);
    const overlayRef = useMergeRefs<HTMLDivElement | null>([
      suggestionsOverlayRef,
      _overlayRef,
    ]);
    const { style: overlayStyles } = _overlayProps;

    // Element refs.
    const triggerRef = useRef<HTMLDivElement | null>(null);
    const editorViewRef = useRef<EditorView | null>(null);
    const internalRootRef = useRef<HTMLDivElement | null>(null);
    const rootRef = useMergeRefs<HTMLDivElement | null>([
      targetRef,
      internalRootRef,
    ]);
    const hiddenInputRef = useRef<HTMLInputElement | null>(null);

    // Error boundary awareness
    const { viewDispatch } = useErrorAwareViewRef(editorViewRef);

    // Linting.
    const filterFieldLinter = useFilterFieldLinting();
    const [hasFormControlError, hint] = useFormControlState(controlState);
    const [hasSyntaxError, setHasSyntaxError] = useState(false);
    const {
      state: formFieldMessageState,
      dispatch: formFieldMessageContextDispatch,
    } = useFormFieldMessagesContext();

    // This state is needed for the correct FilterField error outline and icon
    const [showError, setShowError] = useState(false);
    useEffect(() => {
      if (
        showError &&
        !hasFormControlError &&
        !hasSyntaxError &&
        !formFieldMessageState.showError
      ) {
        setShowError(false);
      }

      if (
        !editorViewRef.current?.hasFocus &&
        formFieldMessageState.isTouched &&
        (hasFormControlError ||
          formFieldMessageState.showError ||
          hasSyntaxError)
      ) {
        setShowError(true);
      }
      // We omit the dependency to showError, as we would run into the
      // effect too often then, as we change this in the effect
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      hasFormControlError,
      hasSyntaxError,
      formFieldMessageState.showError,
      editorViewRef.current?.hasFocus,
    ]);

    // DEPRECATION: [APPDEV-8298] _useInputHint will be replaced with new validation concept.
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    const { inputProps, hintProps } = useInputHint({
      hint,
      hasError: showError,
    });

    // Flag for the form validation to indicate whether the FilterField has been blurred.
    const isDirtyRef = useRef<boolean>(false);

    const {
      controlId,
      required: contextRequired,
      disabled: contextDisabled,
      ariaDisabled: contextAriaDisabled,
      dispatch: formFieldContextDispatch,
    } = useFormFieldContext();

    const disabled = propDisabled || contextDisabled;
    const required = propRequired || contextRequired;
    const readOnly = propReadOnly;

    const ariaDisabled = _parseBoolean(propAriaDisabled) || contextAriaDisabled;

    const [prevDisabled, setPrevDisabled] = useState(disabled);

    if (disabled !== prevDisabled) {
      setPrevDisabled(disabled);
      viewDispatch({
        effects: disabledPropCompartment.reconfigure(
          EditorView.editable.of(!disabled),
        ),
      });
    }

    const [prevReadOnly, setPrevReadOnly] = useState(readOnly);

    if (readOnly !== prevReadOnly) {
      setPrevReadOnly(readOnly);
      viewDispatch({
        effects: readOnlyPropCompartment.reconfigure(
          EditorState.readOnly.of(readOnly || false),
        ),
      });
    }

    // FilterField form control message ids
    const filterFieldCustomErrorRef = useRef<string>(useId());
    const filterFieldRequiredErrorRef = useRef<string>(useId());

    const validate = useCallback(() => {
      // Handle required error
      if (valueRef.current === '' && required) {
        // Add required error.
        formFieldMessageContextDispatch?.({
          type: FormFieldMessagesAction.ADDMESSAGE,
          payload: {
            id: filterFieldRequiredErrorRef.current,
            validityStateKey: 'valueMissing',
            variant: 'error',
            message: (
              <Text textStyle="small" as="p">
                {getI18nErrorMessage('valueMissing', hiddenInputRef.current)}
              </Text>
            ),
            messageType: 'formcontrol',
          },
        });
      } else if (valueRef.current !== '' && required) {
        // clean up required error
        formFieldMessageContextDispatch?.({
          type: FormFieldMessagesAction.REMOVEMESSAGE,
          payload: {
            id: filterFieldRequiredErrorRef.current,
            messageType: 'formcontrol',
          },
        });
      }
    }, [formFieldMessageContextDispatch, required]);

    const updateSyntaxError = useCallback(
      (isValid: boolean) => {
        setHasSyntaxError(!isValid);

        if (!isValid) {
          formFieldMessageContextDispatch?.({
            type: FormFieldMessagesAction.ADDMESSAGE,
            payload: {
              id: filterFieldCustomErrorRef.current,
              validityStateKey: 'customError',
              variant: 'error',
              message: intl.formatMessage({
                defaultMessage: 'Invalid syntax.',
                id: 'ut5dPv/qM9qMEW4a',
                description:
                  'Error message shown when the Input in the FilterField has an invalid syntax.',
              }),
              messageType: 'formcontrol',
            },
          });
        } else {
          formFieldMessageContextDispatch?.({
            type: FormFieldMessagesAction.REMOVEMESSAGE,
            payload: {
              id: filterFieldCustomErrorRef.current,
              messageType: 'formcontrol',
            },
          });
        }
      },
      [formFieldMessageContextDispatch, setHasSyntaxError, intl],
    );

    const onInputChange = () => {
      // only validate on change when there is an error
      if (isDirtyRef.current) {
        validate();
      }
    };

    const onInputBlur = () => {
      // always validate on blur
      isDirtyRef.current = true;
      validate();
      formFieldMessageContextDispatch?.({
        type: FormFieldMessagesAction.SETISTOUCHED,
        payload: { isTouched: true },
      });
    };

    useImperativeHandle(
      forwardedRef,
      (): FormControlWithOverlayRef => ({
        element: internalRootRef.current,
        triggerRef: triggerRef.current,
        inputRef: hiddenInputRef.current,
        focus: () => hiddenInputRef.current?.focus(),
        open: () => hiddenInputRef.current?.focus(),
        close: () => overlayState.close(),
        validate: () => {
          validate();
          formFieldMessageContextDispatch?.({
            type: FormFieldMessagesAction.SETISTOUCHED,
            payload: { isTouched: true },
          });
        },
      }),
      [overlayState, formFieldMessageContextDispatch, validate],
    );

    // The currently entered last term to use for highlighting matching parts of suggestions.
    const suggestionsContext = useRef<
      _FilterFieldSuggestionsContext | undefined
    >(undefined);

    const [
      autoSuggestionValues,
      setAutoSuggestionValues,
      autoSuggestionValuesRef,
    ] = useReferencedState<FilterFieldSuggestion[]>([]);

    const [prevAutoSuggestionValues, setPrevAutoSuggestionValues] = useState<
      FilterFieldSuggestion[]
    >([]);

    const [focusedSuggestion, setFocusedSuggestion] = useState<
      string | undefined
    >();
    // Previously focused suggestion. Used to trigger an update of the attributes
    // compartment when rendering to avoid an additional re-render.
    const prevFocusedSuggestion = usePrevious(focusedSuggestion);

    // Suggestions callback to determine which suggestions to show.
    const _onSuggest: _FilterFieldSuggestionsCallback = useCallback(
      (args) => {
        suggestionsContext.current = args.suggestionsContext;

        if (
          props.autoSuggestions &&
          JSON.stringify(args.autoSuggestionValues) !==
            JSON.stringify(autoSuggestionValuesRef.current)
        ) {
          setAutoSuggestionValues(args.autoSuggestionValues);
        }
      },
      [setAutoSuggestionValues, props.autoSuggestions, autoSuggestionValuesRef],
    );

    // FilterField state.
    const [state, dispatch] = useReducer(filterFieldReducer, {
      suggestions: new Map(),
      activeSuggestion: -1,
    });
    const [hideDeleteWhileTyping, setHideDeleteWhileTyping] = useState(false);

    /**
     * Apply the given suggested value to the document, replacing the selection
     * so that the cursor end up at the end of the inserted text.
     */
    const applySuggestion = useCallback(
      (
        value: string,
        trailing: string | undefined,
        insertionStrategy: FilterFieldSuggestionProps['insertionStrategy'],
        cursorBeforeTrailing: boolean | undefined,
      ) => {
        if (!editorViewRef.current || !suggestionsContext.current) {
          return;
        }

        const suggestionTransaction = getSuggestionTransaction(
          value,
          trailing,
          insertionStrategy,
          cursorBeforeTrailing,
          suggestionsContext.current,
          editorViewRef.current.state,
        );

        // The applied suggestion will always override the last token.
        viewDispatch({
          ...suggestionTransaction,
          annotations: stringAnnotation.of(ANNOTATION_SUGGESTION_APPLY),
        });
        setFocusedSuggestion(undefined);
      },
      [viewDispatch],
    );

    const ariaAttributes = useMemo(
      () => ({
        'aria-label': contentEditableDivAriaLabel,
        'aria-haspopup': 'listbox',
        ...(overlayState.isOpen && {
          'aria-controls': `suggestions-${uniqueId}`,
        }),
        ...(focusedSuggestion && {
          'aria-activedescendant': `${focusedSuggestion}`,
        }),
        ...(ariaDisabled && {
          'aria-disabled': `${ariaDisabled}`,
        }),
        'aria-describedby': 'suggestions-overlay-announcement',
      }),
      [
        contentEditableDivAriaLabel,
        overlayState.isOpen,
        uniqueId,
        focusedSuggestion,
        ariaDisabled,
      ],
    );

    // Reference of the currently applied value, stored in a ref to avoid constantly
    // reconfiguring the editor.
    const valueRef = useRef(value ?? defaultValue ?? '');
    // Reference of the transaction needed to get from the current state to the next
    // state if the filter field is controlled.
    const transactionRef = useRef<TransactionSpec[] | undefined>(undefined);

    /**
     * Effect for configuring the editor.
     * Only called on mount.
     * State variables / stateful functions needed are either stored in refs or updated using codemirror compartments.
     */
    useEffect(() => {
      if (!triggerRef.current) {
        return;
      }

      function handleOnChange(transaction: Transaction) {
        if (!editorViewRef.current) {
          return transaction;
        }

        let newValue = transaction.newDoc.toString();
        let transactions: TransactionSpec[] = [];

        const tree = syntaxTree(transaction.state);

        const { tree: convertedTree, isValid } = getSyntaxTree({
          tree,
          view: editorViewRef.current,
          numberParser,
          doc: transaction.newDoc,
          syntax,
          validatorMap: validatorMapRef?.current,
        });

        // this has to be set regardless of whether the newValue is equal
        // to the valueRef.current as we also want to update the error
        // state when programmatically changing the value
        updateSyntaxError(isValid);
        onInputChange();

        const annotation = transaction.annotation(stringAnnotation);
        if (annotation === 'doc-changed.controlled') {
          return transaction;
        }

        if (newValue !== valueRef.current) {
          setHideDeleteWhileTyping(true);

          // Run auto-formatter for any modifications of the document other than deletions.
          // If something is deleted, the auto-formatter has already been executed when the
          // new document was entered.
          if (
            !transaction.isUserEvent('delete.backward') &&
            !transaction.isUserEvent('delete.forward')
          ) {
            [newValue, transactions] = autoFormatLogicalOperators(
              newValue,
              tree,
              transaction.selection?.ranges[
                transaction.selection?.mainIndex ?? 0
              ].to ?? 0,
            );
          }

          onChangeRef.current?.(newValue, convertedTree, isValid);

          if (transaction.isUserEvent('clear')) {
            onFilterRef.current?.({
              value: '',
              syntaxTree: undefined,
              isValid: true,
            });
          }

          transactionRef.current = [{ selection: transaction.newSelection }];
          const userEvent = transaction.annotation(Transaction.userEvent);
          transactionRef.current = [
            {
              ...transaction,
              // On Windows / Linux, hitting 'Shift + Delete' results in an undefined selection,
              // leading to the suggestions callback not being triggered.
              // Even for an empty value, there should always be a selection.
              selection: transaction.newSelection ?? { anchor: 0 },
              // Annotations are not automatically stored, we need to do that manually
              // to be able to determine, which action triggered the transaction when applying
              // the new value in a controlled scenario.
              annotations: Transaction.userEvent.of(
                userEvent
                  ? userEvent
                  : transaction.annotation(stringAnnotation) ?? '',
              ),
            },
            ...transactions,
          ];

          if (!isControlled) {
            valueRef.current = newValue;
            onInputChange();
          }

          return isControlled ? [] : [transaction, ...transactions];
        }

        return [transaction];
      }

      const initialEditorState = EditorState.create({
        doc: value ?? defaultValue ?? '',
        extensions: [
          /**
           * General
           */
          // Enable line wrapping so that overflowing text is wrapped into the next line.
          EditorView.lineWrapping,
          // Handling internal exceptions
          EditorView.exceptionSink.of((err) => showBoundary(err)),
          // Enable multiple selections (CMD / ctrl + drag).
          EditorState.allowMultipleSelections.of(true),
          // Provides a undo/redo history
          history(),
          // Enable displaying a placeholder text.
          placeholderCompartment.of(
            placeholderExtension(placeholderProp ?? placeholder),
          ),
          drawSelection(),

          keymap.of([
            ...defaultKeymap.filter(
              ({ key }) => !overrideKeys.includes(key || ''),
            ),
            ...closeBracketsKeymap,
            {
              key: 'Escape',
              run: () => {
                overlayState.close();
                return false;
              },
            },
            {
              key: 'Mod-z',
              run: undo,
            },
            {
              key: 'Mod-y',
              run: redo,
            },
            {
              key: 'Mod-Shift-z',
              run: redo,
            },
          ]),

          /**
           * Linting
           */
          filterFieldLinter,
          tooltips({ parent: document.body }),

          /**
           * Filter field language + syntax highlighting.
           *
           * Error highlighting is also done with the highlight extensions, since using an actual
           * linter, potential errors only show up after a short (configurable) delay. As soon as
           * you type something again, the errors disappear and then show up again if the input is
           * still invalid. Using the decoration plugins, we can immediately show errors.
           * The actual linter extension is only used to show tooltips with proper error messages.
           */
          // The extension for parsing the filter field language into a syntax tree.
          filterFieldLanguageExtension,
          // Automatically close brackets.
          closeBrackets(),
          usedSyntaxMode,
          usedValidatorMap,
          usedErrors,

          // Enable highlighting using the defined highlighter.
          // This adds the spans and classes used to style the different parts of a filter.
          syntaxHighlighting(filterFieldHighlighter),
          // Add decoration plugins for wrapping filter groups, statements, keys, and values
          // in additional spans for styling. This is not possible using only a highlighter
          // since the highlighter can only wrap nodes that have no more nesting levels.
          EscapedCharacterDecorationPlugin,
          FilterErrorDecorationPlugin,
          FilterKeyDecorationPlugin,
          FilterVariableDecorationPlugin,
          FilterValueDecorationPlugin,
          FilterExistsDecorationPlugin,
          FilterStatementDeleteWidgetPlugin,
          FilterStatementDecorationPlugin,

          EditorView.domEventObservers({
            compositionend: () => {
              isUserEventComposeRef.current = false;
              editorViewRef.current?.dispatch({
                annotations: stringAnnotation.of(ANNOTATION_COMPOSE_END),
              });
            },
          }),

          /**
           * Suggestions
           */
          suggestionsCompartment.of(
            suggestionsExtension({
              overlayState,
              numberParser,
              onSuggest,
              validatorMap,
              _onSuggest,
              getSyntaxTree,
            }),
          ),

          /**
           * Aria attributes
           */
          ariaAttributesCompartment.of(
            EditorView.contentAttributes.of(ariaAttributes),
          ),

          /**
           * Disabled
           */
          disabledPropCompartment.of(EditorView.editable.of(!propDisabled)),

          /**
           * Read-only
           */
          readOnlyPropCompartment.of(EditorState.readOnly.of(propReadOnly)),

          /**
           * Listeners
           */
          // Intercept transactions that change the doc to not instantly apply the changes
          // in a controlled scenario. Instead, the transaction is stored so we can apply
          // it if the consumer actually updates the value.
          EditorState.transactionFilter.of((transaction) => {
            // Safeguard.
            if (!editorViewRef.current) {
              return transaction;
            }

            // Check annotations for a controlled value update.
            const annotation = transaction.annotation(stringAnnotation);
            if (annotation === 'doc-changed.controlled') {
              return handleOnChange(transaction);
            }

            // If we have a user event (usually some kind of input), we need to check for
            // composition events to properly handle composed characters in a controlled scenario.
            let userEvent = transaction.annotation(Transaction.userEvent);

            // Certain custom transactions should also be treated as user event.
            if (userEvent === undefined) {
              switch (annotation) {
                case ANNOTATION_COMPOSE_END:
                  userEvent = ANNOTATION_COMPOSE_END;
                  break;
                case ANNOTATION_DELETE_STATEMENT:
                  userEvent = ANNOTATION_DELETE_STATEMENT;
                  break;
                case ANNOTATION_SUGGESTION_APPLY:
                  userEvent = ANNOTATION_SUGGESTION_APPLY;
              }
            }

            if (userEvent === 'input.type.compose.start') {
              // Composition event was started. Do not intercept any transaction while ongoing.
              isUserEventComposeRef.current = true;
            }

            // Any transaction not related to a user event should not be handled specifically.
            // Transactions of an ongoing composition should also be forwarded to properly
            // show the composition steps in the input.
            if (!userEvent || isUserEventComposeRef.current) {
              return transaction;
            }

            if (
              (transaction.docChanged && userEvent) ||
              !isUserEventComposeRef.current
            ) {
              return handleOnChange(transaction);
            }

            return transaction;
          }),

          EditorView.updateListener.of((update) => {
            // Check if the transaction is undo or redo in order to not accidentally call onChange
            // twice. Usually onChange is called before applying the changes. However, undo and redo
            // are considered as transactions that occur after the view has been updated.
            // Therefore, we explicitly call onChange if that is the case.
            const isUndoOrRedo = update.transactions.some(
              (transaction) =>
                transaction.isUserEvent('undo') ||
                transaction.isUserEvent('redo'),
            );

            if (update.docChanged && isUndoOrRedo) {
              update.transactions.forEach((transaction) =>
                handleOnChange(transaction),
              );
            }
          }),
        ],
      });

      const editorView = new EditorView({
        state: initialEditorState,
        parent: triggerRef.current,
      });
      editorViewRef.current = editorView;

      if (
        editorView.state.field(usedSyntaxMode, false) !== syntax ||
        validatorMap
      ) {
        // do not use view ref here
        editorView.dispatch({
          effects: [
            syntaxModeEffect.of(syntax),
            validatorMapEffect.of(validatorMapRef?.current),
          ],
          // In order to re-run the decorations and re-generate errors and diagnostics,
          // we have to dispatch a new document.
          changes: {
            from: 0,
            to: editorViewRef.current.state.doc.length,
            insert: editorViewRef.current.state.doc,
          },
        });
      }

      const { isValid } = getSyntaxTree({
        tree: syntaxTree(editorView.state),
        view: editorView,
        numberParser,
        doc: editorView.state.doc,
        syntax,
        validatorMap: validatorMapRef?.current,
      });

      updateSyntaxError(isValid);

      return () => editorView.destroy();
      // Only run for initializing the filter field.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Update the tree and error state if the mode changes.
    if (
      (syntax !== prevSyntaxMode ||
        !isEqual(validatorMap, validatorMapRef.current)) &&
      editorViewRef.current
    ) {
      if (syntax !== prevSyntaxMode) {
        setPrevSyntaxMode(syntax);
      }

      if (!isEqual(validatorMap, validatorMapRef.current)) {
        validatorMapRef.current = validatorMap;
        viewDispatch({
          effects: suggestionsCompartment.reconfigure(
            suggestionsExtension({
              overlayState,
              numberParser,
              onSuggest,
              _onSuggest,
              getSyntaxTree,
              validatorMap,
            }),
          ),
        });

        // With a changed validator map, the suggestions also need to be re-calculated. The completion is
        // triggered in order to run the suggestions extension, in turn calculating the new list of suggestions.
        startCompletion(editorViewRef.current);
      }

      const doc = editorViewRef.current.state.doc;

      queueMicrotask(() => {
        if (editorViewRef.current) {
          const tree = syntaxTree(editorViewRef.current.state);
          const { tree: convertedTree, isValid } = getSyntaxTree({
            tree,
            view: editorViewRef.current,
            numberParser,
            doc,
            syntax,
            validatorMap: validatorMapRef.current,
          });

          updateSyntaxError(isValid);
          onChangeRef.current?.(doc.toString(), convertedTree, isValid);
          onInputChange();

          viewDispatch({
            effects: [
              syntaxModeEffect.of(syntax),
              validatorMapEffect.of(validatorMapRef?.current),
            ],
            // In order to re-run the decorations and re-generate errors and diagnostics,
            // we have to dispatch a new document.
            changes: {
              from: 0,
              to: editorViewRef.current.state.doc.length,
              insert: doc.toString(),
            },
          });
        }
      });
    }

    // Update configuration compartments that are depending on props when the props change.
    if (placeholderProp !== prevPlaceholderProp) {
      viewDispatch({
        effects: placeholderCompartment.reconfigure(
          placeholderExtension(placeholderProp ?? placeholder),
        ),
      });

      setPrevPlaceholderProp(placeholderProp);
    }

    // Re-configure the suggestions extension to use a potentially changed callback.
    if (onSuggest !== prevOnSuggest) {
      viewDispatch({
        effects: suggestionsCompartment.reconfigure(
          suggestionsExtension({
            overlayState,
            numberParser,
            onSuggest,
            _onSuggest,
            getSyntaxTree,
            validatorMap,
          }),
        ),
      });

      setPrevOnSuggest(() => onSuggest);
    }

    // Dispatch a transaction to update the editor's doc to match the prop
    // value if it changes and the filter field is controlled.
    // Since the value may have been adjusted in the `onChange` callback, we replace the whole document.
    // FIXME: the cursor position jumps in case the value's length changes in the controlled state update.
    if (value !== prevValue && isControlled) {
      setPrevValue(value);
      valueRef.current = value;

      // Wait for any previous codemirror transactions to go through, else it will
      // throw an error that we are trying to update a state that doesn't start
      // from the previous state.
      queueMicrotask(() => {
        onInputChange();
        const lastTransaction =
          transactionRef.current &&
          transactionRef.current[transactionRef.current.length - 1];
        const selection =
          lastTransaction?.selection ??
          // Fall back to the current editor selection in case the last transaction is undefined
          // to avoid a jumping cursor when typing really fast.
          editorViewRef.current?.state.selection;

        const anchor = Math.min(
          value.length,
          (isEditorSelection(selection)
            ? selection.ranges[0].anchor
            : selection?.anchor) ?? value.length,
        );

        viewDispatch({
          changes: {
            from: 0,
            to: editorViewRef.current?.state.doc.length ?? 0,
            insert: value,
          },
          annotations: stringAnnotation.of('doc-changed.controlled'),
          selection: { anchor },
        });

        transactionRef.current = undefined;
      });
    }

    if (
      propDisabled !== prevPropDisabled ||
      propRequired !== prevPropRequired ||
      propReadOnly !== prevPropReadOnly ||
      propId !== prevPropId
    ) {
      setPrevPropDisabled(propDisabled);
      setPrevPropRequired(propRequired);
      setPrevPropReadOnly(propReadOnly);
      setPrevPropId(propId);

      formFieldContextDispatch?.({
        type: FormFieldAction.UPDATE,
        payload: {
          id: propId,
          disabled: propDisabled,
          required: propRequired,
          ariaDisabled: _parseBoolean(propAriaDisabled),
        },
      });

      // Update disabled if disabled prop changes
      if (propDisabled !== prevPropDisabled) {
        viewDispatch({
          effects: disabledPropCompartment.reconfigure(
            EditorView.editable.of(!propDisabled),
          ),
        });
      }

      // Update read-only if read-only prop changes
      if (propReadOnly !== prevPropReadOnly) {
        viewDispatch({
          effects: readOnlyPropCompartment.reconfigure(
            EditorState.readOnly.of(propReadOnly),
          ),
        });
      }
    }

    if (prevOpen && !overlayState.isOpen) {
      // Defer triggering the onSuggest callback, else the filter field is still rendering
      // while the callback may already update the suggestions, which would lead to a runtime error.
      // (Cannot update a component while rendering a different component)
      queueMicrotask(() => {
        onSuggest?.();
      });
    }

    // Update the currently focused suggestion.
    if (
      prevOpen !== overlayState.isOpen ||
      autoSuggestionValuesRef.current !== prevAutoSuggestionValues
    ) {
      if (overlayState.isOpen) {
        // If the currently focused suggestion is still in the document, do nothing.
        if (
          !getCurrentSuggestion(
            suggestionsOverlayRef.current,
            focusedSuggestion,
          )
        ) {
          setFocusedSuggestion(undefined);
        }
      }
      setPrevAutoSuggestionValues(autoSuggestionValuesRef.current ?? []);
      setPrevOpen(overlayState.isOpen);
    }

    // Update aria attributes if overlay state or focused suggestion changes.
    if (
      prevFocusedSuggestion !== focusedSuggestion ||
      prevOpen !== overlayState.isOpen
    ) {
      viewDispatch({
        effects: ariaAttributesCompartment.reconfigure(
          EditorView.contentAttributes.of(ariaAttributes),
        ),
      });
    }

    const handleNextSuggestion = (nextSuggestion: HTMLElement | null) => {
      if (nextSuggestion) {
        setFocusedSuggestion(
          nextSuggestion.dataset.stratoFilterFieldSuggestionId,
        );
        nextSuggestion.scrollIntoView({ block: 'center' });
      }
    };

    const handleClearButton = () => {
      if (editorViewRef.current) {
        viewDispatch({
          changes: {
            from: 0,
            to: editorViewRef.current.state.doc.length,
            insert: '',
          },
          annotations: Transaction.userEvent.of('clear'),
        });
      }
    };

    const autoSuggestionsConfig =
      useMemo((): FilterFieldAutoSuggestionsContextProps => {
        const filterValue = suggestionsContext.current?.currentStatement?.value;
        const cursorPosition = editorViewRef.current?.state.selection.main.head;

        return {
          trailing: cursorPosition === filterValue?.range.to ? ' ' : undefined,
          suggestions: autoSuggestionValues,
        } as FilterFieldAutoSuggestionsContextProps;
      }, [autoSuggestionValues]);

    return (
      <FilterFieldContext.Provider
        value={{
          applySuggestion,
          dispatch,
          focusedSuggestion,
          filterFieldId: uniqueId,
          term: suggestionsContext.current?.token.textValue,
          autoSuggestions: autoSuggestions ? autoSuggestionsConfig : {},
          triggerRef: triggerRef.current ?? null,
        }}
      >
        <Flex
          ref={rootRef}
          data-testid={dataTestId}
          data-dtrum-mask={dataDtrumMask}
          data-dtrum-allow={dataDtrumAllow}
          gap={4}
          flexDirection="column"
          className={clsx(styles.wrapper, consumerClassName)}
          style={consumerStyle}
          onMouseMove={() => {
            setHideDeleteWhileTyping(false);
          }}
        >
          <div
            {...inputProps}
            aria-invalid={
              controlState ? inputProps['aria-invalid'] : hasSyntaxError
            }
            className={clsx(
              editorContainerCSS({
                size: 'default',
                fullHeight: false,
                // check if the editor is disabled first to apply proper styling
                hasError: !(disabled || ariaDisabled) && showError,
                lineNumbers: 'hidden',
                readOnly: disabled || readOnly,
              }),
              styles.container,
              props.className,
              {
                [styles.errorText]: showError,
                [styles.errorVars]: showError,
                [styles.disabled]: disabled,
                [styles.readOnly]: readOnly,
                [styles.ariaDisabled]: ariaDisabled,
                hideDeleteWhileTyping,
              },
            )}
            onBlur={onInputBlur}
            onKeyDown={(event: ReactKeyboardEvent) => {
              // If readOnly is set, only allow the tab key to be pressed.
              // This moves the focus from within the editor to the next focusable element.
              // Also check if CTRL is pressed to still allow shortcuts like CTRL+'C'.
              if (readOnly && event.key !== 'Tab' && !event.ctrlKey) {
                event.preventDefault();
                return;
              }
              switch (event.key) {
                case 'Space':
                  if (!editorViewRef.current) {
                    return;
                  }

                  if (event.ctrlKey || event.altKey) {
                    overlayState.open();
                    startCompletion(editorViewRef.current);
                  }
                  break;
                case 'Enter': {
                  event.preventDefault();

                  if (event.shiftKey || !editorViewRef.current) {
                    return;
                  }

                  if (!focusedSuggestion || event.ctrlKey || event.metaKey) {
                    validate();

                    const { tree, isValid } = getSyntaxTree({
                      tree: syntaxTree(editorViewRef.current.state),
                      view: editorViewRef.current,
                      numberParser,
                      doc: editorViewRef.current.state.doc,
                      syntax,
                      validatorMap: validatorMapRef?.current,
                    });

                    onFilterRef.current?.({
                      value: valueRef.current,
                      syntaxTree: tree,
                      isValid,
                    });

                    overlayState.close();

                    if (isValid && hiddenInputRef.current?.form) {
                      let inputCount = 0;
                      for (const element of Array.from(
                        hiddenInputRef.current.form.elements,
                      )) {
                        // Check if the filter field is the only visible form control.
                        // Elements included are listed here: https://developer.mozilla.org/en-US/docs/Web/API/HTMLFormElement/elements#value
                        if (
                          !element.hasAttribute('hidden') &&
                          ((element.tagName === 'INPUT' &&
                            element.getAttribute('type') !== 'hidden') ||
                            element.tagName === 'SELECT' ||
                            element.tagName === 'TEXTAREA')
                        ) {
                          inputCount += 1;

                          if (inputCount > 1) {
                            break;
                          }
                        }
                      }

                      if (inputCount === 1) {
                        hiddenInputRef.current?.form?.requestSubmit();
                      }
                    } else {
                      // We have to wait for the form field to set the validity of the form control.
                      // Then reporting the validity triggers the 'invalid' event, which can be captured on the form.
                      setTimeout(() => {
                        hiddenInputRef.current?.form?.reportValidity();
                      });
                    }
                  } else if (focusedSuggestion) {
                    state.suggestions.get(focusedSuggestion)?.();
                    return;
                  }

                  break;
                }
              }

              // FIXME: the suggestions overlay may be empty, then we shouldn't handle the keys.
              if (overlayState.isOpen) {
                if (overrideKeys.includes(event.key)) {
                  event.preventDefault();
                  event.stopPropagation();
                }

                switch (event.key) {
                  case 'ArrowUp': {
                    const nextSuggestion = getNextSuggestion(
                      'previous',
                      suggestionsOverlayRef.current,
                      focusedSuggestion,
                    );
                    handleNextSuggestion(nextSuggestion);
                    break;
                  }
                  case 'ArrowDown': {
                    const nextSuggestion = getNextSuggestion(
                      'next',
                      suggestionsOverlayRef.current,
                      focusedSuggestion,
                    );
                    handleNextSuggestion(nextSuggestion);
                    break;
                  }
                }
              }
            }}
            onKeyDownCapture={(event: ReactKeyboardEvent) => {
              if (
                (event.key === 'Home' ||
                  event.key === 'End' ||
                  event.key === 'PageUp' ||
                  event.key === 'PageDown') &&
                focusedSuggestion
              ) {
                event.stopPropagation();
                event.preventDefault();

                let nextSuggestion: HTMLElement | null = null;

                if (event.key === 'PageUp') {
                  nextSuggestion = getNextSuggestion(
                    'previous',
                    suggestionsOverlayRef.current,
                    focusedSuggestion,
                    5,
                  );
                } else if (event.key === 'PageDown') {
                  nextSuggestion = getNextSuggestion(
                    'next',
                    suggestionsOverlayRef.current,
                    focusedSuggestion,
                    5,
                  );
                } else if (event.key === 'Home') {
                  nextSuggestion = getFirstSuggestion(
                    suggestionsOverlayRef.current,
                  );
                } else if (event.key === 'End') {
                  nextSuggestion = getLastSuggestion(
                    suggestionsOverlayRef.current,
                  );
                }

                handleNextSuggestion(nextSuggestion);
              }
            }}
            onMouseDown={(event) => {
              if (disabled) {
                event.preventDefault();
              }
            }}
          >
            <span
              className={styles.filterIcon({
                hasError: showError,
                disabled: disabled || Boolean(ariaDisabled),
              })}
            >
              {/*need to keep data-testid to wait for the error icon in the vis-reg test*/}
              {showError ? (
                <CriticalIcon data-testid="strato-filter-field-error-icon" />
              ) : (
                <FilterIcon data-testid="strato-filter-field-filter-icon" />
              )}
            </span>
            <div ref={triggerRef} style={{ maxHeight: 'inherit' }} />
            {valueRef.current && (
              <span className={styles.clearButton}>
                <Button
                  size="condensed"
                  onClick={handleClearButton}
                  aria-label={intl.formatMessage({
                    defaultMessage: 'Clear filter',
                    id: 'mS6CPzO+pMJLetSA',
                    description:
                      'Aria label used for the clear button inside the filter field.',
                  })}
                  color={
                    !(disabled || Boolean(ariaDisabled)) && showError
                      ? 'critical'
                      : 'neutral'
                  }
                  style={{ borderRadius: Borders.Radius.Field.Subdued }}
                  className={styles.clearButtonIcon({
                    hasError: showError,
                    disabled: disabled || Boolean(ariaDisabled),
                  })}
                  disabled={disabled || readOnly}
                >
                  <Button.Prefix>
                    <XmarkIcon />
                  </Button.Prefix>
                </Button>
              </span>
            )}
          </div>
          <SuggestionsOverlay
            ref={overlayRef}
            data-testid={
              dataTestId ? `filterfield-suggestions-${dataTestId}` : undefined
            }
            // The editor can still be focused if readOnly is set.
            // Since the content is not editable, we prevent the overlay from opening.
            isOpen={!readOnly && overlayState.isOpen}
            overlayProps={{ style: overlayStyles }}
            data-dtrum-mask={dataDtrumMask}
            data-dtrum-allow={dataDtrumAllow}
          >
            {suggestionsSlot === undefined ? (
              <Suggestions>{children}</Suggestions>
            ) : (
              children
            )}
          </SuggestionsOverlay>
          {hint && (
            <BaseItem
              variant={showError ? 'error' : 'hint'}
              disabled={disabled || ariaDisabled}
              {...hintProps}
            >
              {hint}
            </BaseItem>
          )}
          <VisuallyHidden>
            <input
              ref={hiddenInputRef}
              data-testid="hidden-input"
              name={name}
              value={valueRef.current}
              required={required}
              disabled={disabled}
              readOnly={readOnly}
              tabIndex={-1}
              id={propId ?? controlId}
              onFocus={() => {
                // The focus should move to the FilterField when clicking on
                // the label (when using it in a FormField)
                editorViewRef.current?.focus();
              }}
              onChange={() => {
                /** noop */
              }}
              onInvalid={(event) => {
                event.preventDefault();
                event.currentTarget.focus();
                onInputBlur();
              }}
              {...ariaLabelingProps}
            />
          </VisuallyHidden>
          <p
            id="suggestions-overlay-announcement"
            className={styles.suggestionsOverlayAnnouncement}
          >
            {intl.formatMessage({
              defaultMessage:
                'Suggestions overlay opened. You can navigate to a suggestion with the arrow up or arrow down key and select it by pressing enter. Please continue typing to filter.',
              id: '1cD2DGUXvVHfcjKy',
            })}
          </p>
        </Flex>
      </FilterFieldContext.Provider>
    );
  },
);

(_FilterField as typeof FilterField & { displayName: string }).displayName =
  'FilterField';

/**
 * FilterField component that uses a fallback formFieldMessages-context if it wasn't wrapped in a FormField.
 * @internal
 */
export const InternalFilterField = withFormFieldMessageContextProvider<
  FilterFieldProps,
  FormControlWithOverlayRef
>(
  forwardRef<FormControlWithOverlayRef, FilterFieldProps>(
    (props, forwardedRef) => (
      <ErrorBoundary
        FallbackComponent={(fallbackProps) => (
          <ErrorFallback
            {...fallbackProps}
            currentState={props.value ?? props.defaultValue ?? ''}
          />
        )}
      >
        {/* eslint-disable-next-line react/jsx-pascal-case */}
        <_FilterField {...props} ref={forwardedRef} />
      </ErrorBoundary>
    ),
  ),
);

/**
 * The `FilterField` component is a text-based input component that allows users
 * to enter filters using an intuitive and easily understandable syntax.
 * Since the input is text-based, users can always enter anything they want.
 * To provide guidance to the user, we recommend always using the `FilterField`
 * in combination with suggestions.
 * @public
 */
export const FilterField = Object.assign(InternalFilterField, {
  Suggestions,
  Suggestion,
  SuggestionGroup,
  SuggestionGroupLabel,
  SuggestionDetails,
});
